import { useState, useEffect } from 'react';

// Global singleton pattern for script management
const GoogleMapsLoader = {
  SCRIPT_ID: 'google-maps-script',
  isLoading: false,
  isLoaded: false,
  loadPromise: null as Promise<void> | null,
  callbacks: new Set<() => void>(),

  createScript(): HTMLScriptElement {
    const script = document.createElement('script');
    script.id = this.SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    return script;
  },

  load(): Promise<void> {
    if (this.isLoaded) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    // Remove any existing script
    const existingScript = document.getElementById(this.SCRIPT_ID);
    if (existingScript) {
      document.head.removeChild(existingScript);
    }

    this.isLoading = true;
    this.loadPromise = new Promise<void>((resolve, reject) => {
      const script = this.createScript();

      script.onload = () => {
        this.isLoaded = true;
        this.isLoading = false;
        this.callbacks.forEach(callback => callback());
        this.callbacks.clear();
        resolve();
      };

      script.onerror = (error) => {
        this.isLoading = false;
        this.loadPromise = null;
        reject(error);
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  },

  addCallback(callback: () => void) {
    if (this.isLoaded) {
      callback();
    } else {
      this.callbacks.add(callback);
    }
  },

  removeCallback(callback: () => void) {
    this.callbacks.delete(callback);
  }
};

export function useGoogleMaps() {
  const [loaded, setLoaded] = useState(GoogleMapsLoader.isLoaded);

  useEffect(() => {
    // Check if Google Maps is already available
    if (window.google?.maps) {
      GoogleMapsLoader.isLoaded = true;
      setLoaded(true);
      return;
    }

    const handleLoad = () => setLoaded(true);
    GoogleMapsLoader.addCallback(handleLoad);

    if (!GoogleMapsLoader.isLoading && !GoogleMapsLoader.isLoaded) {
      GoogleMapsLoader.load().catch(console.error);
    }

    return () => {
      GoogleMapsLoader.removeCallback(handleLoad);
    };
  }, []);

  return loaded;
}

// Add TypeScript type declaration for window.google
declare global {
  interface Window {
    google?: {
      maps: any;
    };
  }
}